import { useState } from 'react';
import { Card, Button, Form } from 'react-bootstrap';
import { FuelIntervalResponse } from '../../models';

export type EditableInterval = FuelIntervalResponse & { id: number };

interface Props {
  interval: EditableInterval;
  onDelete: (id: number) => void;
  onCarbsChange: (id: number, value: number) => void;
  onKmBlur: (id: number, value: number) => void;
  onHover: (id: number | null) => void;
}


export default function FuelPointCard({
  interval,
  onDelete,
  onCarbsChange,
  onKmBlur,
  onHover,
}: Props) {
  const [carbsEditMode, setCarbsEditMode] = useState(false);
  const [carbsValue, setCarbsValue] = useState(String(interval.targetCarbs));
  const [kmEditMode, setKmEditMode] = useState(false);
  const [kmValue, setKmValue] = useState(interval.kilometerMark.toFixed(1));
  const commitKm = (raw: string) => {
    const num = parseFloat(raw);
    if (!isNaN(num)) onKmBlur(interval.id, num);
    setKmValue(interval.kilometerMark.toFixed(1));
    setKmEditMode(false);
  };

  const commitCarbs = (raw: string) => {
    const num = parseInt(raw, 10);
    if (!isNaN(num)) {
      const clamped = Math.max(0, Math.min(1000, num));
      onCarbsChange(interval.id, clamped);
      setCarbsValue(String(clamped));
    }
    setCarbsEditMode(false);
  };

  return (
    <Card
      className="mb-2"
      onMouseEnter={() => onHover(interval.id)}
      onMouseLeave={() => onHover(null)}
    >
      <Card.Body className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <div className="km-stepper">
              <button
                className="km-stepper-btn"
                onClick={() => onKmBlur(interval.id, Math.max(0, interval.kilometerMark - 5))}
              >−</button>
              {kmEditMode ? (
                <Form.Control
                  type="number"
                  step="0.1"
                  value={kmValue}
                  onChange={(e) => setKmValue(e.target.value)}
                  onBlur={() => commitKm(kmValue)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitKm(kmValue);
                    else if (e.key === 'Escape') setKmEditMode(false);
                  }}
                  autoFocus
                  className="no-spinners"
                  style={{ width: 80, fontSize: '1.3rem', fontWeight: 'bold', textAlign: 'center', padding: '0 0.4rem', border: 'none', background: 'transparent', color: '#fff', outline: 'none', boxShadow: 'none' }}
                />
              ) : (
                <span
                  className="km-stepper-value"
                  onClick={() => { setKmValue(interval.kilometerMark.toFixed(1)); setKmEditMode(true); }}
                >
                  {interval.kilometerMark.toFixed(1)}
                </span>
              )}
              <button
                className="km-stepper-btn"
                onClick={() => onKmBlur(interval.id, interval.kilometerMark + 5)}
              >+</button>
            </div>
            <span className="text-secondary" style={{ fontSize: '0.9rem' }}>km</span>
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-between">
          <Button
            variant="outline-primary"
            onClick={() => onCarbsChange(interval.id, Math.max(0, interval.targetCarbs - 5))}
            className="p-2"
            style={{ width: 44, height: 44 }}
          >
            −
          </Button>
          {carbsEditMode ? (
            <Form.Control
              type="text"
              inputMode="numeric"
              value={carbsValue}
              onChange={(e) => setCarbsValue(e.target.value)}
              onBlur={() => commitCarbs(carbsValue)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitCarbs(carbsValue);
                else if (e.key === 'Escape') setCarbsEditMode(false);
              }}
              autoFocus
              style={{ width: 80, textAlign: 'center', fontSize: '1.3rem', fontWeight: 'bold' }}
            />
          ) : (
            <span
              className="fw-bold"
              style={{ fontSize: '1.3rem', minWidth: 80, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
              onClick={() => setCarbsEditMode(true)}
            >
              {interval.targetCarbs} g
            </span>
          )}
          <Button
            variant="outline-primary"
            onClick={() => onCarbsChange(interval.id, Math.min(1000, interval.targetCarbs + 5))}
            className="p-2"
            style={{ width: 44, height: 44 }}
          >
            +
          </Button>
          <button
            type="button"
            className="btn delete-icon-btn"
            onClick={() => onDelete(interval.id)}
            title="Usuń punkt"
          >
            <i className="bi bi-trash"></i>
          </button>
        </div>
      </Card.Body>
    </Card>
  );
}
